import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@shared/ui/accordion";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { Edit, Plus, Search, Settings, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  useDeleteSystemType,
  useGetSystemType,
  useGetSystemTypeParameter,
  useSearchSystemType,
} from "../../hooks";
import { SystemTypeDialog } from "../create/create";
import { ParameterDialog } from "../createParameters/createParameters";
import { logEvent } from "@shared/analytics/analytics";

interface SystemType {
  code: string;
  name: string;
  description: string;
  parameters: any[];
}

export const SystemTypesList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSystemType, setSelectedSystemType] = useState("");
  const [selectedSystemTypeForParams, setSelectedSystemTypeForParams] =
    useState<SystemType | null>(null);
  const [showParameters, setShowParameters] = useState(false);
  const [expandedAccordionItem, setExpandedAccordionItem] =
    useState<string>("");

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [systemTypeToDelete, setSystemTypeToDelete] =
    useState<SystemType | null>(null);

  const { data: systemTypesData } = useGetSystemType("system_type", true);
  const { data: systemTypesSearchData } = useSearchSystemType(
    "system_json-system_type",
    searchTerm
  );

  const showSystemTypesData = systemTypesSearchData || systemTypesData;
  const { data: systemTypeParameterData } = useGetSystemTypeParameter(
    "system_parameter",
    true,
    selectedSystemType
  );

  const { mutate: deleteSystemType, isPending: isDeletingSystemType } =
    useDeleteSystemType();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSystemType, setEditingSystemType] = useState<SystemType | null>(
    null
  );

  const handleAccordionChange = (value: string) => {
    setExpandedAccordionItem(value);
    setSelectedSystemType(value);
  };
  const handleAddClick = () => {
    logEvent("system_type_add_clicked");
    setEditingSystemType(null);
    setIsDialogOpen(true);
  };

  const handleEditSystemType = (systemType: SystemType) => {
    logEvent("system_type_edit_clicked", { code: systemType.code });
    setEditingSystemType(systemType);
    setIsDialogOpen(true);
  };
  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingSystemType(null);
  };

  const handleDeleteClick = (systemType: SystemType) => {
    logEvent("system_type_delete_initiated", { code: systemType.code });
    setSystemTypeToDelete(systemType);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (systemTypeToDelete) {
      logEvent("system_type_delete_confirmed", {
        code: systemTypeToDelete.code,
      });
      deleteSystemType({
        remote_utility_id: getRemoteUtilityId(),
        code: systemTypeToDelete.code,
      });
    }
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setSystemTypeToDelete(null);
  };

  const handleManageParameters = (systemType: SystemType) => {
    logEvent("system_type_manage_parameters_clicked", {
      code: systemType.code,
    });
    setSelectedSystemTypeForParams(systemType);
    setShowParameters(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between gap-4 mt-5">
        <div>
          <Input
            id="search"
            placeholder="Search system types..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-96"
          />
        </div>
        <Button onClick={handleAddClick} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add System Type
        </Button>
      </div>

      <Card>
        {showSystemTypesData && showSystemTypesData.length > 0 ? (
          <Accordion
            type="single"
            collapsible
            className="w-full"
            value={expandedAccordionItem}
            onValueChange={handleAccordionChange}
          >
            {showSystemTypesData?.map((systemType) => (
              <AccordionItem key={systemType?.code} value={systemType?.code}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex flex-col items-start gap-2 flex-1">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-blue-600">
                          {systemType?.name}
                        </span>
                        <Badge variant="secondary">
                          {systemType?.childCount} parameters
                        </Badge>
                      </div>
                      {systemType?.description && (
                        <div className="text-left w-full">
                          <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md border-l-4 border-blue-200">
                            <span className="font-medium text-gray-800">
                              Description:{" "}
                            </span>
                            {systemType.description}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSystemType(systemType);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(systemType);
                        }}
                        disabled={true}
                        tooltip={
                          "This functionality is currently not available."
                        }
                        tooltipPosition="left"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-4">
                    {/* Manage Parameters Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleManageParameters(systemType)}
                      className="flex items-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Manage Parameters
                    </Button>

                    {/* Parameters Preview */}
                    <div className="space-y-3">
                      {/* Show parameters from API if available, otherwise show local parameters */}
                      {selectedSystemType === systemType?.code &&
                      systemTypeParameterData?.length > 0 ? (
                        <div className="space-y-2">
                          <h4 className="font-medium text-sm text-gray-700">
                            Monitored Parameters:
                          </h4>
                          <div className="text-sm text-gray-600">
                            {systemTypeParameterData
                              ?.slice(0, 5)
                              .map((param: any) => (
                                <span
                                  key={param.code}
                                  className="inline-block mr-2 mb-1"
                                >
                                  {param.name} ({param.unit})
                                </span>
                              ))}
                            {systemTypeParameterData?.length > 5 && (
                              <span className="text-gray-500">
                                and {systemTypeParameterData?.length - 5}{" "}
                                more...
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          No parameters defined yet.
                        </p>
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <CardContent className="text-center py-12">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-medium text-gray-900">
                  No System Types Available
                </h3>
                <p className="text-sm text-gray-500 max-w-sm">
                  {searchTerm
                    ? `No system types found matching "${searchTerm}". Try adjusting your search or create a new system type.`
                    : "Get started by creating your first system type to organize and monitor your water treatment parameters."}
                </p>
              </div>
              <Button
                onClick={handleAddClick}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create System Type
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Single Dialog for Both Add and Edit */}
      <SystemTypeDialog
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        editData={editingSystemType}
        searchTerm={searchTerm}
      />

      {/* Parameter Management Dialog */}
      <ParameterDialog
        showParameters={showParameters}
        setShowParameters={setShowParameters}
        selectedSystemTypeForParams={selectedSystemTypeForParams}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        title="Delete System Type"
        itemName={systemTypeToDelete?.name}
        description={`Are you sure you want to delete "${systemTypeToDelete?.name}"? This will permanently remove the system type and all its associated parameters. This action cannot be undone.`}
        isLoading={isDeletingSystemType}
      />
    </div>
  );
};
